<script setup lang="ts">
import { NConfigProvider, NMessageProvider, NDialogProvider, darkTheme, zhCN, dateZhCN } from 'naive-ui'
</script>

<template>
  <NConfigProvider :locale="zhCN" :date-locale="dateZhCN" :theme="darkTheme" :theme-overrides="themeOverrides">
    <NMessageProvider>
      <NDialogProvider>
        <RouterView />
      </NDialogProvider>
    </NMessageProvider>
  </NConfigProvider>
</template>

<script lang="ts">
export const themeOverrides = {
  common: {
    primaryColor: '#00f0ff',
    primaryColorHover: '#33f3ff',
    primaryColorPressed: '#00c8d6',
    primaryColorSuppl: '#00dde8',
    borderRadius: '8px',
    bodyColor: '#050a14',
    cardColor: '#0a1628',
    modalColor: '#0a1628',
    popoverColor: '#111d33',
    tableColor: '#0a1628',
    inputColor: '#111d33',
    actionColor: '#111d33',
    borderColor: 'rgba(0,240,255,0.12)',
    dividerColor: 'rgba(0,240,255,0.12)',
    hoverColor: 'rgba(0,240,255,0.08)',
    textColorBase: '#e8f0fe',
    textColor1: '#e8f0fe',
    textColor2: '#8899bb',
    textColor3: '#5a6a88',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, "Noto Sans SC", sans-serif',
  },
  Card: {
    color: 'rgba(10,22,40,0.7)',
    borderColor: 'rgba(0,240,255,0.12)',
    titleTextColor: '#00f0ff',
    titleFontSizeMedium: '13px',
    titleFontWeight: '700',
    borderRadius: '12px',
  },
  Statistic: {
    valueFontSize: '30px',
    valueFontWeight: '800',
    valueTextColor: '#00f0ff',
    labelTextColor: '#8899bb',
    labelFontSize: '12px',
  },
  List: {
    color: 'transparent',
    borderColor: 'rgba(0,240,255,0.08)',
    textColor: '#e8f0fe',
    fontSize: '13px',
  },
  Empty: {
    textColor: '#5a6a88',
  },
  Layout: {
    color: '#050a14',
    siderColor: '#050a14',
    headerColor: '#050a14',
  },
  Menu: {
    color: 'transparent',
    itemTextColor: '#8899bb',
    itemTextColorHover: '#00f0ff',
    itemTextColorActive: '#00f0ff',
    itemIconColor: '#5a6a88',
    itemIconColorHover: '#00f0ff',
    itemIconColorActive: '#00f0ff',
    itemColorActive: 'rgba(0,240,255,0.08)',
    itemColorActiveHover: 'rgba(0,240,255,0.12)',
    dividerColor: 'rgba(0,240,255,0.08)',
  },
  Button: {
    borderRadiusMedium: '8px',
    borderRadiusLarge: '10px',
  },
  Input: {
    color: '#111d33',
    borderColor: 'rgba(0,240,255,0.12)',
    borderHover: '#00f0ff',
    borderFocus: '#00f0ff',
    textColor: '#e8f0fe',
    placeholderColor: '#5a6a88',
    caretColor: '#00f0ff',
  },
  Tag: {
    borderRadius: '20px',
    colorSuccess: 'rgba(34,197,94,0.12)',
    borderSuccess: 'rgba(34,197,94,0.3)',
    textColorSuccess: '#22c55e',
    colorWarning: 'rgba(245,158,11,0.12)',
    borderWarning: 'rgba(245,158,11,0.3)',
    textColorWarning: '#f59e0b',
    colorInfo: 'rgba(0,240,255,0.12)',
    borderInfo: 'rgba(0,240,255,0.3)',
    textColorInfo: '#00f0ff',
  },
  Progress: {
    railColor: 'rgba(0,240,255,0.08)',
    fillColor: '#00f0ff',
  },
  DataTable: {
    thColor: '#0a1628',
    tdColor: 'rgba(10,22,40,0.7)',
    borderColor: 'rgba(0,240,255,0.08)',
    thTextColor: '#8899bb',
    tdTextColor: '#e8f0fe',
  },
  Dropdown: {
    color: '#111d33',
    optionTextColor: '#e8f0fe',
    optionTextColorHover: '#00f0ff',
    optionColorHover: 'rgba(0,240,255,0.08)',
  },
  Alert: {
    colorInfo: 'rgba(0,240,255,0.06)',
    borderInfo: 'rgba(0,240,255,0.2)',
    iconColorInfo: '#00f0ff',
    titleTextColorInfo: '#00f0ff',
    contentTextColorInfo: '#8899bb',
  },
}
</script>

<style>
:root {
  --cyan: #00f0ff;
  --cyan-d: rgba(0,240,255,0.2);
  --purple: #a855f7;
  --purple-d: rgba(168,85,247,0.2);
  --blue: #3b82f6;
  --green: #22c55e;
  --red: #ef4444;
  --gold: #f59e0b;
  --d0: #000;
  --d1: #050a14;
  --d2: #0a1628;
  --d3: #111d33;
  --d4: #182847;
  --txt: #e8f0fe;
  --txt2: #8899bb;
  --glass: rgba(10,22,40,0.7);
  --gb: rgba(0,240,255,0.12);
  --fm: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, 'Noto Sans SC', sans-serif;
  --mono: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace;
}
html,
body,
#app {
  margin: 0;
  padding: 0;
  height: 100%;
  font-family: var(--fm);
  background: var(--d1);
  color: var(--txt);
}
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: var(--d1); }
::-webkit-scrollbar-thumb { background: var(--cyan-d); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: rgba(0,240,255,0.35); }
</style>
